import React, { FC, useMemo } from 'react'
import { get } from 'lodash'
import { FieldRenderProps } from 'react-final-form'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText, InputLabel } from '@mui/material'

import { IconType } from '../../../types/interface/icon.interface'
import { BrandIconShared } from '@dg-shared'
import { ThemeBrandColorsType } from 'BrandTheme/types'

const useRFFCustomDatePickerStyles = makeStyles()((theme: Theme) => ({
  formControl: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },

  inputLabel: {
    position: 'relative',
    color: theme.palette.info.dark,
    fontSize: theme.spacing(2),
    lineHeight: 1.4, // 22
  },

  datePicker: {
    display: 'flex',
    '& > div': {
      color: theme.palette.primary.dark,
    },
    marginTop: theme.spacing(2),
    marginBottom: 0,
  },

  iconCustomClass: {
    ...theme.brand.assets.layout?.iconCustomColor,
  },
}))

// What the form configs feed in: Date objects, `Date.now()` numbers, or date strings.
type ParsableDate = Date | number | string | null | undefined

// v4 pickers parsed any of these; the v7 date-fns adapter accepts only Date.
const toDate = (value: ParsableDate): Date | null =>
  value === null || value === undefined || value === '' ? null : new Date(value)

interface RFFCustomDatePickerProps extends FieldRenderProps<ParsableDate> {
  labelId?: string
  fieldLabel?: string
  fieldPlaceholder?: string
  fieldMinDate?: ParsableDate
  fieldMaxDate?: ParsableDate
  fieldInputVariant?: 'standard' | 'outlined' | 'filled'
  formatType?: string
  // Only the keys this component reads off it; the hook that supplies it is wider.
  validationArgs?: {
    min?: ParsableDate
    max?: ParsableDate
    disabled?: boolean
  }
  iconColor?: keyof ThemeBrandColorsType
}

const RFFCustomDatePicker: FC<RFFCustomDatePickerProps> = ({
  input,
  meta,
  labelId,
  fieldLabel,
  fieldPlaceholder,
  fieldMinDate,
  fieldMaxDate,
  fieldInputVariant,
  formatType,
  validationArgs,
  iconColor,
}) => {
  const { classes } = useRFFCustomDatePickerStyles()
  const isError: boolean = (meta.error || meta.submitError) && !meta.pristine

  // Use calculated validation min and max date or default fieldMinDate
  const minDate = get(validationArgs, 'min') || fieldMinDate
  const maxDate = get(validationArgs, 'max') || fieldMaxDate
  const disabled = get(validationArgs, 'disabled', false)

  // A component (not an element) for the `openPickerIcon` slot; memoised so the
  // picker does not remount the icon on every render.
  const CalendarIcon = useMemo(
    () =>
      function CalendarIcon() {
        return (
          <BrandIconShared
            customClass={classes.iconCustomClass}
            type={IconType.CALENDAR}
            color={iconColor}
          />
        )
      },
    [classes.iconCustomClass, iconColor]
  )

  return (
    <FormControl variant='standard' error={isError} className={classes.formControl}>
      {!!fieldLabel && (
        <InputLabel id={labelId} className={classes.inputLabel} shrink>
          {fieldLabel}
        </InputLabel>
      )}
      {/* Desktop variant everywhere: v4's inline KeyboardDatePicker allowed typing on
          every device, while the responsive DatePicker turns read-only on touch. */}
      <DesktopDatePicker<Date>
        disabled={disabled}
        format={formatType}
        value={toDate(input.value)}
        onChange={input.onChange}
        minDate={toDate(minDate) || undefined}
        maxDate={toDate(maxDate) || undefined}
        slots={{ openPickerIcon: CalendarIcon }}
        slotProps={{
          textField: {
            type: input.type || 'tel',
            variant: fieldInputVariant || 'standard',
            margin: 'normal',
            className: classes.datePicker,
            placeholder: fieldPlaceholder,
            helperText: '', // Need to disable component validation
            // The picker consumes `input` rather than spreading it, so the rendered
            // <input> carries no `name` and cannot be located the way every other
            // field is (`[name="..."]`). Give it an explicit hook instead of leaving
            // tests to select it by exclusion.
            inputProps: { 'data-testid': `date-${input.name}` },
          },
          // v7 sets edge='end' (a -12px margin) on this button; v4's sat inside the field.
          openPickerButton: { 'aria-label': fieldLabel, edge: false },
        }}
      />
      <FormHelperText id='date-picker-error-label'>{meta.error}</FormHelperText>
    </FormControl>
  )
}

export default RFFCustomDatePicker
